// =============================================================================
// io/sync/leakUniverse.ts — the trades the Coach's Report measures.
// -----------------------------------------------------------------------------
// ── WHY A FOURTH TRADES READ ──
//
// None of the three existing reads can serve this, and the gap is not small:
//
//   loadTradeMetrics   11 columns. No `time_out`, no `side`, no `extras`, no
//                      `rr_planned`, no `risk_at_entry`, no `mistake_tags`.
//                      Four of the six measurable leaks cannot run on it.
//   loadTradesInRange  the trades TABLE's columns — no `mistake_tags`, and it
//                      exists to draw a table rather than to measure behaviour.
//   loadCoachingRows   four columns over the whole book, for two sentences.
//
// So this is its own read, justified the same way `coaching.ts` is: each column
// earns its place because a leak stops measuring without it.
//
// ── 🚨 THE REPORT OWNS ITS WINDOW, AND IT IS NOT THE GLOBAL FILTER ──
//
// The old client is explicit: the loop "owns its OWN date window and must never
// ride the global activeDateRange or the analytics-local analyticsState — the
// user moves those." A rule proved over "the last 30 days" has to keep meaning
// the last 30 days when the trader filters the Trades tab to last March.
//
// ── ⚠ THE COST, STATED RATHER THAN DISCOVERED ──
//
// This pulls `extras` — which carries the fills — so it is the widest read in
// the client per row. Over a 7d or 30d window that is nothing. On **All** it is
// the whole book with the fill detail attached, which is the mobile-transfer
// cost D47 measured. The report's default window is 30d for that reason, and
// `All` is a deliberate choice the trader makes rather than where they land.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { PAGE_ROWS } from './load';

/**
 * The columns, and what stops working without each.
 *
 *   date, account_id      the window and the account picker
 *   contract              names a trade in the adherence review list, where the
 *                         trader is judging one specific trade against a rule
 *   time_in, time_out     hold time (disposition), and fill ordering
 *   side                  which direction an add-down is
 *   entry, exit           the review list's own columns. A leak's drill exists to
 *                         answer "what actually happened on these trades", and
 *                         the price you got in and out at is where that starts
 *   net_pnl               every leak
 *   points, qty           deriving R when it was not logged
 *   planned_stop          the price holding would have taken you out at — the
 *                         premature-exit leak's stop leg, and there is no other
 *                         source for it
 *   risk_points           R basis for capture and for plan shortfall
 *   rr_planned            the plan leak's target — absent from every other read
 *   rr_realized           what was actually taken
 *   risk_at_entry         what one R was worth in dollars
 *   mae_points, mfe_points     capture efficiency, and the E-ratio — the
 *                         dimension read that says whether an edge exists at all
 *   pattern_id, exit_model_id  the pattern x exit pairing dimension. A pairing is
 *                         a CELL; neither id alone can produce one
 *   confluences           which reads carry the edge, ranked by median R
 *   booleans              the manual controllable-loss override
 *   mistake_tags          the controllable-loss classifier
 *   controllable_loss_amount   the dollar face of that leak
 *   extras                the FILLS, for averaging down — and the per-rule
 *                         adherence marks, which Prove is judged on
 *
 * ⚠ Each one is here because dropping it changes a number. That is the same bar
 * `DOMAIN_COLUMNS` sets, and it is worth re-stating because a wide select is
 * where columns quietly accumulate.
 */
export const LEAK_COLUMNS: readonly string[] = [
  'id', 'account_id', 'date', 'time_in', 'time_out', 'side', 'contract',
  'entry', 'exit',
  'net_pnl', 'points', 'qty', 'planned_stop', 'risk_points', 'rr_planned',
  'rr_realized',
  'risk_at_entry', 'mae_points', 'mfe_points',
  'pattern_id', 'exit_model_id', 'confluences',
  'booleans', 'mistake_tags', 'controllable_loss_amount', 'extras',
];

export const LEAK_SELECT: string = LEAK_COLUMNS.join(',');

export interface LeakUniverseTrade {
  id: string;
  accountId: string | null;
  date: string;
  contract: string | null;
  timeIn: string | null;
  timeOut: string | null;
  side: string | null;
  entry: number | null;
  exit: number | null;
  netPnl: number | null;
  points: number | null;
  qty: number | null;
  plannedStop: number | null;
  riskPoints: number | null;
  rrPlanned: number | null;
  rrRealized: number | null;
  riskAtEntry: number | null;
  maePoints: number | null;
  mfePoints: number | null;
  patternId: string | null;
  exitModelId: string | null;
  confluences: Record<string, unknown> | null;
  booleans: Record<string, boolean> | null;
  mistakeTags: readonly string[] | null;
  controllableLossAmount: number | null;
  executions: readonly { time: string | null; side: string | null; qty: number | null; price: number | null }[] | null;
  /**
   * `{ [ruleId]: true|false }` — the trader's own answer to "did I follow that
   * rule here?", per rule.
   *
   * ⚠ RIDES `extras`, like the fills. Reading it off the row itself returns
   * undefined and every rule reports "no trades marked" — which stops the
   * verdict at `unmeasured` forever, on a rule the trader has been marking all
   * month. Silent, and indistinguishable from having done nothing.
   */
  ruleAdherences: Record<string, unknown> | null;
  /** The pre-multi single mark. Read so an old one is not treated as unmarked. */
  ruleAdherence: { ruleId?: unknown; followed?: unknown } | null;
  /**
   * The stored post-exit walk, keyed by window.
   *
   * ⚠ RIDES `extras`, like the fills and the adherence marks. It is written by
   * a metered bar fetch in the old client, so a trade that was never recomputed
   * simply has none — and the premature-exit leak drops it from its population
   * rather than reading it as a zero.
   */
  extensions: Record<string, Record<string, unknown>> | null;
}

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function leakTradeFromDb(r: Record<string, unknown>): LeakUniverseTrade {
  const extras = (r['extras'] && typeof r['extras'] === 'object')
    ? r['extras'] as Record<string, unknown>
    : {};
  // ⚠ `executions` RIDES `extras` — it is not a column. Reading it off the row
  // itself returns undefined and the averaging-down leak silently measures
  // nothing, which reads as "you never average down".
  const ex = Array.isArray(extras['executions']) ? extras['executions'] : null;

  return {
    id: String(r['id'] ?? ''),
    accountId: (r['account_id'] as string | null) ?? null,
    date: String(r['date'] ?? ''),
    contract: (r['contract'] as string | null) ?? null,
    timeIn: (r['time_in'] as string | null) ?? null,
    timeOut: (r['time_out'] as string | null) ?? null,
    side: (r['side'] as string | null) ?? null,
    entry: num(r['entry']),
    exit: num(r['exit']),
    netPnl: num(r['net_pnl']),
    points: num(r['points']),
    qty: num(r['qty']),
    plannedStop: num(r['planned_stop']),
    riskPoints: num(r['risk_points']),
    rrPlanned: num(r['rr_planned']),
    rrRealized: num(r['rr_realized']),
    riskAtEntry: num(r['risk_at_entry']),
    maePoints: num(r['mae_points']),
    mfePoints: num(r['mfe_points']),
    patternId: (r['pattern_id'] as string | null) ?? null,
    exitModelId: (r['exit_model_id'] as string | null) ?? null,
    confluences: (r['confluences'] && typeof r['confluences'] === 'object')
      ? r['confluences'] as Record<string, unknown>
      : null,
    booleans: (r['booleans'] && typeof r['booleans'] === 'object')
      ? r['booleans'] as Record<string, boolean>
      : null,
    mistakeTags: Array.isArray(r['mistake_tags']) ? r['mistake_tags'] as string[] : null,
    // ⚠ Beta stores this as a POSITIVE magnitude on a losing trade. Kept as
    // stored; the leak takes its absolute value rather than assuming a sign.
    controllableLossAmount: num(r['controllable_loss_amount']),
    executions: ex as LeakUniverseTrade['executions'],
    ruleAdherences: (extras['ruleAdherences'] && typeof extras['ruleAdherences'] === 'object')
      ? extras['ruleAdherences'] as Record<string, unknown>
      : null,
    ruleAdherence: (extras['ruleAdherence'] && typeof extras['ruleAdherence'] === 'object')
      ? extras['ruleAdherence'] as { ruleId?: unknown; followed?: unknown }
      : null,
    extensions: (extras['extensions'] && typeof extras['extensions'] === 'object')
      ? extras['extensions'] as Record<string, Record<string, unknown>>
      : null,
  };
}

/**
 * Every trade in the report's window.
 *
 * @param since inclusive ISO date, or null for the whole book.
 *
 * ⚠ PAGES TO EXHAUSTION and THROWS on a bad page. A short read here does not
 * look short — it looks like a smaller leak, which is the one direction this
 * report must never be wrong in. The trader would conclude a habit had improved.
 */
export async function loadLeakUniverse(
  sb: SupabaseClient, since: string | null,
): Promise<LeakUniverseTrade[]> {
  const out: LeakUniverseTrade[] = [];

  for (let from = 0; ; from += PAGE_ROWS) {
    let q = sb.from('trades').select(LEAK_SELECT);
    if (since) q = q.gte('date', since);
    const { data, error } = await q
      .order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);

    if (error) {
      throw new Error(
        `The Coach's Report could not read your trades (page ${(from / PAGE_ROWS) + 1}): `
        + `${error.message}`,
      );
    }
    if (!data || !data.length) break;
    for (const r of data as unknown as Record<string, unknown>[]) out.push(leakTradeFromDb(r));
    if (data.length < PAGE_ROWS) break;
  }

  return out;
}
